import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { loginSchema } from '@/lib/validation';
import { authApi, ApiError } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';
import {
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
    LoginResponseSchema,
    JsonValuePrunedSchema,
} from '@pillar-portal/api-types';

const loginPageFormSchema = z.object({
    username: loginSchema.shape.email,
    password: loginSchema.shape.password,
});

type LoginPageFormData = z.infer<typeof loginPageFormSchema>;

export default function LoginPage() {
    const log = createComponentLogger('LoginPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefilledEmail: string = searchParams.get('email')?.trim() ?? '';
    const paymentRequestUuid: string = searchParams.get('payment_request_uuid')?.trim() ?? '';
    const returnToPayment: boolean = searchParams.get('return_to_payment') === '1';
    const queryClient = useQueryClient();
    const { user, logout: logoutStore, setUser } = useAuthStore();
    const [error, setError] = useState<string>('');
    const [isRedirecting, setIsRedirecting] = useState(false);
    const hasAttemptedExistingSessionPaymentLinkAttach = useRef(false);
    // Check if user already has a valid session
    const { data: existingProfile, isLoading: isCheckingSession } =
        useQuery<SessionUserProfileDetailedResponse | null>({
            queryKey: ['profile'],
            queryFn: async () => {
                const response = await authApi.getProfile();
                if (response === null || response === undefined) {
                    return null;
                }
                return SessionUserProfileDetailedResponseSchema.parse(response);
            },
            enabled: !user, // Only check if user is not already in store
            retry: false,
            staleTime: 0,
            gcTime: 0,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            refetchOnReconnect: false,
        });

    // Redirect to dashboard or the payment page if user is already authenticated
    useEffect(() => {
        if (user || existingProfile) {
            if (existingProfile && !user) {
                // If we fetched profile but user is not in store, set it
                setUser(existingProfile);
            }
            if (
                paymentRequestUuid.length > 0
                && !hasAttemptedExistingSessionPaymentLinkAttach.current
            ) {
                hasAttemptedExistingSessionPaymentLinkAttach.current = true;
                if (returnToPayment) {
                    setIsRedirecting(true);
                    runInBackground(navigate('/pay', { replace: true }), (err) => {
                        setIsRedirecting(false);
                        setError(
                            err instanceof Error && err.message.trim().length > 0
                                ? err.message
                                : 'Could not return to the payment page.'
                        );
                        log.error('Error returning to payment page', err);
                    });
                    return;
                }
                setIsRedirecting(true);
                runInBackground(
                    authApi
                        .completeCurrentCustomerPaymentLinkAttachment({
                            payment_request_uuid: paymentRequestUuid,
                        })
                        .then(() => navigate('/dashboard', { replace: true })),
                    (err) => {
                        setIsRedirecting(false);
                        setError(
                            err instanceof Error && err.message.trim().length > 0
                                ? err.message
                                : 'Could not attach the paid payment request to your account.'
                        );
                        log.error('Error attaching paid payment request to current session', err);
                    }
                );
                return;
            }
            runInBackground(
                navigate(returnToPayment ? '/pay' : '/dashboard', { replace: true }),
                (err) => {
                log.error('Error navigating to dashboard', err);
                }
            );
        }
    }, [
        existingProfile,
        log,
        navigate,
        paymentRequestUuid,
        returnToPayment,
        setUser,
        user,
    ]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginPageFormData>({
        resolver: zodResolver(loginPageFormSchema),
        defaultValues: {
            username: prefilledEmail,
            password: '',
        },
    });

    const loginMutation = useMutation({
        mutationFn: async (data: LoginPageFormData) => {
            const response = await authApi.login({
                email: data.username,
                password: data.password,
                ...(!returnToPayment && paymentRequestUuid.length > 0
                    ? { payment_request_uuid: paymentRequestUuid }
                    : {}),
            });
            return LoginResponseSchema.parse(response);
        },
        onSuccess: async (data) => {
            // Clear any stale cache from previous user session
            queryClient.clear();
            // Clear Zustand store to ensure fresh state
            logoutStore();
            setIsRedirecting(true);
            if (data.requires_totp) {
                const nextSearchParams = new URLSearchParams();
                if (paymentRequestUuid.length > 0) {
                    nextSearchParams.set('payment_request_uuid', paymentRequestUuid);
                }
                if (returnToPayment) {
                    nextSearchParams.set('return_to_payment', '1');
                }
                const nextTotpPath =
                    nextSearchParams.toString().length > 0
                        ? `/login/totp?${nextSearchParams.toString()}`
                        : '/login/totp';
                runInBackground(navigate(nextTotpPath), (err) => {
                    log.error('Error navigating to TOTP page', err);
                });
            } else {
                // When TOTP is not required, fetch profile to get complete user data including roles
                // then set user in store before redirecting to the post-login destination.
                try {
                    const profileResponse = await authApi.getProfile();
                    if (profileResponse) {
                        const profileData =
                            SessionUserProfileDetailedResponseSchema.parse(profileResponse);
                        setUser(profileData);
                    }
                    window.location.assign(returnToPayment ? '/pay' : '/dashboard');
                } catch (err) {
                    // If profile fetch fails, still redirect and let ProtectedRoute handle it
                    // This ensures we don't get stuck on login page
                    log.error('Failed to fetch profile after login', err);
                    window.location.assign(returnToPayment ? '/pay' : '/dashboard');
                }
            }
        },
        onError: (err: Error) => {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError(`Error: ${JsonValuePrunedSchema.parse(err)}`);
            }
            log.error('Error logging in', err);
        },
    });

    const onSubmit = (data: LoginPageFormData) => {
        setError('');
        loginMutation.mutate(data);
    };

    if (error.length > 0 && (user || existingProfile)) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                <Card className="w-full max-w-md">
                    <CardHeader className="space-y-1">
                        <CardTitle>Account linking unavailable</CardTitle>
                        <CardDescription>
                            We could not attach this paid payment request to your current account.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                            {error}
                        </div>
                        <Button
                            type="button"
                            className="w-full"
                            onClick={() => navigate('/dashboard', { replace: true })}
                        >
                            Continue to dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show loading screen while checking for existing session or during redirect
    if (isCheckingSession || isRedirecting || user || existingProfile) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                            <p className="text-sm text-muted-foreground">
                                {isRedirecting ? 'Signing you in...' : 'Checking session...'}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-3xl font-bold text-center flex justify-center">
                        <AnchorbaseLogo width={280} />
                    </CardTitle>
                    <CardDescription className="text-center">
                        Enter your credentials to access your account
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="username">Email</Label>
                            <Input
                                id="username"
                                type="email"
                                placeholder="you@example.com"
                                autoComplete="username"
                                {...register('username')}
                                readOnly={prefilledEmail.length > 0}
                                disabled={loginMutation.isPending}
                            />
                            {errors.username && (
                                <p className="text-sm text-destructive">
                                    {errors.username.message}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                {...register('password')}
                                disabled={loginMutation.isPending}
                            />
                            {errors.password && (
                                <p className="text-sm text-destructive">
                                    {errors.password.message}
                                </p>
                            )}
                        </div>

                        {error && (
                            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                                {error}
                            </div>
                        )}

                        <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                            {loginMutation.isPending ? 'Signing in...' : 'Sign In'}
                        </Button>

                        <div className="text-center">
                            <a
                                href="/forgot-password"
                                className="text-sm text-primary hover:underline"
                            >
                                Forgot Password?
                            </a>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
